import React from 'react';
import { Box, Typography, CardContent, Divider, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { BRAND_COLORS, SEMANTIC_COLORS } from '../../styles/theme';
import { AnalysisResultCard } from './styles';
import { useTranslation } from '../../i18n';

interface AnalysisResultProps {
  analysisResult?: {
    success: boolean;
    metadata: Record<string, unknown>;
    rows_processed: number;
    columns_processed: number;
  };
  onClose?: () => void;
}

const AnalysisResult: React.FC<AnalysisResultProps> = ({ analysisResult, onClose }) => {
  const { t } = useTranslation();

  if (!analysisResult || !analysisResult.success) {
    return null;
  }

  const { metadata, rows_processed, columns_processed } = analysisResult;

  return (
    <AnalysisResultCard>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" sx={{ color: BRAND_COLORS.forestGreen.main }}>
            {t('fileManagement.flightAnalysisResults')}
          </Typography>
          {onClose && (
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: SEMANTIC_COLORS.text.secondary }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="body2" color="textSecondary">
              {t('fileManagement.rowsProcessed')}
            </Typography>
            <Typography variant="h6">{rows_processed}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="textSecondary">
              {t('fileManagement.columnsProcessed')}
            </Typography>
            <Typography variant="h6">{columns_processed}</Typography>
          </Box>
        </Box>

        {metadata && Object.keys(metadata).length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ color: BRAND_COLORS.forestGreen.main }}
            >
              {t('fileManagement.extractedMetadata')}
            </Typography>
            <Box sx={{ maxHeight: '300px', overflow: 'auto' }}>
              <pre
                style={{
                  fontSize: '0.875rem',
                  color: SEMANTIC_COLORS.text.secondary,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {JSON.stringify(metadata, null, 2)}
              </pre>
            </Box>
          </>
        )}
      </CardContent>
    </AnalysisResultCard>
  );
};

export default AnalysisResult;
